import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { godeskApi } from '../api';
import toast from 'react-hot-toast';

export default function SupportTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = async () => {
    try {
      const { data } = await godeskApi.getTickets();
      setTickets(data || []);
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleSelectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setReplies([]);
    try {
      const { data } = await godeskApi.getTicketReplies(ticket.id);
      setReplies(data || []);
    } catch (err) {
      toast.error('Failed to load replies');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;
    setSubmitting(true);
    try {
      await godeskApi.replyToTicket(selectedTicket.id, { message: newReply });
      setNewReply('');
      const { data } = await godeskApi.getTicketReplies(selectedTicket.id);
      setReplies(data || []);
      fetchTickets();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    try {
      await godeskApi.closeTicket(selectedTicket.id);
      toast.success('Ticket closed');
      setSelectedTicket({ ...selectedTicket, status: 'closed' });
      fetchTickets();
    } catch (err) {
      toast.error('Failed to close ticket');
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Support Tickets</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage bugs and feature requests from child panels.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-180px)]">
        {/* Ticket List */}
        <div className="w-full lg:w-1/3 card flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/20 font-semibold tracking-tight text-foreground">Inbox</div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {loading ? (
              <div className="p-6 text-center text-muted-foreground text-sm animate-pulse">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">No tickets found.</div>
            ) : (
              tickets.map(ticket => (
                <div 
                  key={ticket.id} 
                  className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${selectedTicket?.id === ticket.id ? 'bg-muted border-l-4 border-primary' : 'border-l-4 border-transparent'}`}
                  onClick={() => handleSelectTicket(ticket)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${ticket.type === 'bug' ? 'bg-destructive/10 text-destructive border-transparent' : 'bg-secondary text-secondary-foreground border-transparent'}`}>
                      {ticket.type.toUpperCase()}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${ticket.status === 'open' ? 'bg-secondary text-secondary-foreground border-transparent' : ticket.status === 'closed' ? 'bg-muted text-muted-foreground border-transparent' : 'bg-secondary text-secondary-foreground border-transparent'}`}>
                      {ticket.status.toUpperCase()}
                    </span>
                  </div>
                  <h3 className="font-medium text-foreground text-sm mb-1 line-clamp-1">{ticket.subject}</h3>
                  <div className="text-xs text-muted-foreground truncate">{ticket.panel_domain}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Ticket View */}
        <div className="w-full lg:w-2/3 card flex flex-col overflow-hidden">
          {selectedTicket ? (
            <>
              <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
                <div>
                  <h2 className="text-xl font-bold tracking-tight text-foreground">{selectedTicket.subject}</h2>
                  <p className="text-sm text-muted-foreground mt-1">Panel: <span className="font-medium text-foreground">{selectedTicket.panel_domain}</span> • Date: {new Date(selectedTicket.created_at).toLocaleString()}</p>
                </div>
                {selectedTicket.status !== 'closed' && (
                  <button onClick={handleClose} className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2">
                    Close Ticket
                  </button>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-muted/10">
                {/* Original Message */}
                <div className="flex flex-col gap-1.5 items-start">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">Original Report</div>
                  <div className="bg-background p-4 rounded-md border border-border text-foreground whitespace-pre-wrap text-sm w-full">
                    {selectedTicket.message}
                  </div>
                </div>

                {/* Replies */}
                {replies.map(reply => (
                  <div key={reply.id} className={`flex flex-col gap-1.5 w-full ${reply.sender !== 'panel' ? 'items-end' : 'items-start'}`}>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                      {reply.sender === 'manager' ? 'You' : reply.sender} • {new Date(reply.created_at).toLocaleString()}
                    </div>
                    <div className={`p-4 rounded-md border whitespace-pre-wrap text-sm max-w-[85%] ${reply.sender !== 'panel' ? 'bg-primary text-primary-foreground border-transparent' : 'bg-background text-foreground border-border'}`}>
                      {reply.message}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-6 border-t border-border bg-background">
                  <form onSubmit={handleReply} className="flex flex-col gap-3">
                    <textarea 
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                      rows="3"
                      required
                      placeholder="Type your reply here..."
                      value={newReply}
                      onChange={e => setNewReply(e.target.value)}
                    ></textarea>
                    <div className="flex justify-end">
                      <button 
                        type="submit" 
                        disabled={submitting || !newReply.trim()} 
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                      >
                        {submitting ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <svg className="w-16 h-16 mb-4 text-muted/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm font-medium text-muted-foreground">Select a ticket to view conversation</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
